/**
 * The Blocking list as the merchant reads it: a few questions Studio cannot answer alone, and
 * everything else folded into one line of tidy-ups.
 *
 * A question is a brand split-off offer ("Tide Simply sits away from the rest of Tide. Split it
 * into its own brand?") or a size-tier question ("Tide Simply 92 fl oz is tagged Medium but
 * stands with the Small packs on the base. Treat it as Small?") or a size layout question ("Tide
 * Unscented: ... How should the store planograms lay it out?"). Two offers that vouch for each
 * other (all Baby beside ARM & HAMMER Baby) are one question: the base stands them together, so
 * they are answered together.
 */
import { SIZE_LAYOUT_PREFIX, SIZE_TIER_ATTR } from "./sizeTiers";
import { SIZE_TIER_QUESTION_PREFIX } from "./sizeTierQuestions";
import type { Flag, FlagQuestion } from "./studioTypes";

export type QuestionFlag = Flag & { question: FlagQuestion };

export interface StudioQuestion {
  /** The flag id for a single offer; both ids for a pair. Also the panel's selection id. */
  id: string;
  /** One offer, or two that vouch for each other, in list order. */
  flags: readonly QuestionFlag[];
}

export interface TidyUpBrand {
  brand: string;
  count: number;
}

export interface TidyUps {
  flags: readonly Flag[];
  /** The brands with the most tidy-ups, most first. */
  brands: readonly TidyUpBrand[];
  /** Tidy-ups under every other brand, and those above the brand level. */
  others: number;
}

export interface StudioQuestionOptions {
  /** The brand attribute: only a split-off that retags this attribute is a question. */
  brandAttr?: string;
  /** The brand a flag sits under, or null when its scope stops above the brand. */
  brandOf: (flag: Flag) => string | null;
  /** How many brands the tidy-up line names before "others". */
  topBrands?: number;
}

export const PAIR_QUESTION_PREFIX = "pair:";
/** Between a pair's two flag ids: a character no brand or flag id carries. */
const PAIR_SEPARATOR = "\u001f";

/** The flag ids a question id stands for: both offers for a pair, else the id itself. */
export const flagIdsOfQuestionId = (id: string): string[] =>
  id.startsWith(PAIR_QUESTION_PREFIX)
    ? id.slice(PAIR_QUESTION_PREFIX.length).split(PAIR_SEPARATOR)
    : [id];

/** A split-off offer that retags the brand, a size-tier question that retags the size tier, or a
 *  size layout question (answered with a layout, not a fix). */
export const isQuestionFlag = (flag: Flag, brandAttr?: string): flag is QuestionFlag =>
  (flag.id.startsWith(SIZE_LAYOUT_PREFIX) && flag.question?.sizeLayout !== undefined) ||
  (flag.fix?.type === "retagItems" &&
    flag.question !== undefined &&
    (flag.id.startsWith(SIZE_TIER_QUESTION_PREFIX)
      ? flag.fix.attr === SIZE_TIER_ATTR
      : flag.id.startsWith("split-off:") &&
        (brandAttr === undefined || flag.fix.attr === brandAttr)));

const vouchesFor = (a: QuestionFlag, b: QuestionFlag): boolean =>
  a.question.voucher !== undefined && b.skuIds.includes(a.question.voucher.skuId);

export const groupStudioQuestions = (
  flags: readonly Flag[],
  { brandAttr, brandOf, topBrands = 4 }: StudioQuestionOptions,
): { questions: StudioQuestion[]; tidyUps: TidyUps } => {
  const offers = flags.filter((flag): flag is QuestionFlag => isQuestionFlag(flag, brandAttr));
  const paired = new Set<string>();
  const questions: StudioQuestion[] = [];
  for (const offer of offers) {
    if (paired.has(offer.id)) continue;
    const partner = offers.find(
      (other) =>
        other.id !== offer.id &&
        !paired.has(other.id) &&
        vouchesFor(offer, other) &&
        vouchesFor(other, offer),
    );
    paired.add(offer.id);
    if (partner) {
      paired.add(partner.id);
      questions.push({
        id: `${PAIR_QUESTION_PREFIX}${offer.id}${PAIR_SEPARATOR}${partner.id}`,
        flags: [offer, partner],
      });
    } else {
      questions.push({ id: offer.id, flags: [offer] });
    }
  }

  const tidy = flags.filter((flag) => !paired.has(flag.id));
  const counts = new Map<string, number>();
  let unbranded = 0;
  for (const flag of tidy) {
    const brand = brandOf(flag);
    if (brand) counts.set(brand, (counts.get(brand) ?? 0) + 1);
    else unbranded += 1;
  }
  const ranked = [...counts.entries()]
    .map(([brand, count]) => ({ brand, count }))
    .sort((a, b) => b.count - a.count || a.brand.localeCompare(b.brand));
  const brands = ranked.slice(0, topBrands);
  const others = unbranded + ranked.slice(topBrands).reduce((sum, entry) => sum + entry.count, 0);
  return { questions, tidyUps: { flags: tidy, brands, others } };
};
